import { useRef } from "react";
import toast from "react-hot-toast";
import { startCase } from "lodash";

import { BoxedContent } from "@/components/common";
import { DynamicFormContainer } from "@/components/dynamic";
import dynamicObjectMap from "@/utils/maps/dynamicObjectMap";
import { getDynamicObjectRecordById } from "@/api/dynamic/dynamicObjectNameApi";
import { formatDecimalValues } from "@/utils/helpers";

const formatRentalInvoiceDetail = (rentalInvoice) => {
  const rentalDetail = (rentalInvoice?.rentalDetail || [])
    .map((payment) => ({
      contract: {
        label: payment?.contract?.number,
        value: payment?.contract?.id,
      },
      building: {
        label: payment?.building?.name,
        value: payment?.building?.id,
      },
      component: {
        label: payment?.contract?.component?.name,
        value: payment?.contract?.component?.id,
      },
      unit: payment?.unit?.map((unit) => ({
        label: unit?.name,
        value: unit?.id,
      })),
      chequeNo: payment.chequeNo || "",
      paymentDate: new Date(payment.paymentDate),
      rentAmount: payment.rentAmount,
      utilityAmount: payment.utilityAmount,
      securityDepositAmount: payment.securityDepositAmount,
      adminChargesAmount: payment.adminChargesAmount,
      amount: payment.amount,
      paymentMethod: {
        label: startCase(payment.paymentMethod),
        value: payment.paymentMethod,
      },
      tax: {
        label: payment?.tax?.name,
        value: payment?.tax?.id,
        saleRate: payment?.tax?.saleRate,
      },
      taxAmount: payment.taxAmount,
      totalAmount: payment.totalAmount,
      description: payment.description,
      paymentDetailId: payment.paymentDetailId,
    }))
    .sort((a, b) => b.paymentDate - a.paymentDate);

  return {
    rentalDetail,
    subtotal: rentalInvoice?.subtotal,
    taxAmount: rentalInvoice?.taxAmount,
    total: formatDecimalValues(rentalInvoice?.total),
  };
};

const formatGeneralInvoiceDetail = (invoice) => {
  const {
    component, lineItem, date, dueDate, subtotal, amountBeforeTax, taxAmount, total
  } = invoice || {};

  const rentalDetail = (lineItem || []).map((item) => ({
    account: {
      label: item?.account?.name,
      value: item?.account?.id,
    },
    component: {
      label: component?.name,
      value: component?.id,
    },
    paymentDate: new Date(dueDate || date),
    amount: item.amount,
    generalInvoiceAmount: item.amount,
    tax: {
      label: item?.tax?.name,
      value: item?.tax?.id,
      saleRate: item?.tax?.saleRate,
    },
    taxAmount: item.taxAmount,
    totalAmount: item.totalAmount,
    description: item.description,
  }));

  return {
    rentalDetail,
    subtotal,
    amountBeforeTax,
    taxAmount,
    total: formatDecimalValues(total || 0),
  };
};

const emptyInvoiceSelection = {
  rentalDetail: [],
  subtotal: null,
  amountBeforeTax: null,
  taxAmount: null,
  total: null,
};

function CreditNoteForm() {
  const ref = useRef(null);

  const applyRentalInvoice = async (rentalInvoiceValue) => {
    try {
      const rentalInvoice = await getDynamicObjectRecordById(
        dynamicObjectMap.get("RentalInvoiceObjectName"),
        rentalInvoiceValue.value,
      );

      // Bail out if the user picked a different rental invoice while this was in flight.
      if (ref.current.getState().rentalInvoice?.value !== rentalInvoiceValue.value) {
        return;
      }

      if (rentalInvoice?.status === "Reversed") {
        toast.error("Cannot select a reversed rental invoice");
        ref.current.setFormValue("rentalInvoice", null);
        ref.current.setFormState(emptyInvoiceSelection);
        return;
      }

      const paymentDetailIds = [
        ...new Set(
          (rentalInvoice?.rentalDetail || [])
            .map((detail) => detail.paymentDetailId)
            .filter(Boolean),
        ),
      ];

      const paymentDetails = await Promise.all(
        paymentDetailIds.map((paymentDetailId) =>
          getDynamicObjectRecordById(
            dynamicObjectMap.get("ContractPaymentDetailObjectName"),
            paymentDetailId,
          )),
      );

      // Bail out if the user picked a different rental invoice while this was in flight.
      if (ref.current.getState().rentalInvoice?.value !== rentalInvoiceValue.value) {
        return;
      }

      const isPaymentMade = paymentDetails.some(
        (paymentDetail) =>
          paymentDetail?.paymentStatus && paymentDetail.paymentStatus !== "Pending",
      );

      if (isPaymentMade) {
        toast.error("Cancel Payment Receipt first");
        ref.current.setFormValue("rentalInvoice", null);
        ref.current.setFormState(emptyInvoiceSelection);
        return;
      }

      ref.current.setFormState(formatRentalInvoiceDetail(rentalInvoice));
    } catch (error) {
      toast.error("Could not validate the selected rental invoice, please try again");
      ref.current.setFormValue("rentalInvoice", null);
      ref.current.setFormState(emptyInvoiceSelection);
    }
  };

  const applyGeneralInvoice = async (generalInvoiceValue) => {
    try {
      const generalInvoice = await getDynamicObjectRecordById(
        dynamicObjectMap.get("GeneralInvoiceObjectName"),
        generalInvoiceValue.value,
      );

      // Bail out if the user picked a different general invoice while this was in flight.
      if (ref.current.getState().generalInvoice?.value !== generalInvoiceValue.value) {
        return;
      }

      if (generalInvoice?.status === "Reversed") {
        toast.error("Cannot select a reversed general invoice");
        ref.current.setFormValue("generalInvoice", null);
        ref.current.setFormState(emptyInvoiceSelection);
        return;
      }

      if (["Partial", "Paid"].includes(generalInvoice?.status)) {
        toast.error("Cancel General Receipt first");
        ref.current.setFormValue("generalInvoice", null);
        ref.current.setFormState(emptyInvoiceSelection);
        return;
      }

      ref.current.setFormState(formatGeneralInvoiceDetail(generalInvoice));
    } catch (error) {
      toast.error("Could not validate the selected general invoice, please try again");
      ref.current.setFormValue("generalInvoice", null);
      ref.current.setFormState(emptyInvoiceSelection);
    }
  };

  const onStateChange = (key, value) => {
    switch (key) {
      case "tenant":
      case "invoiceType":
        ref.current.setFormState({
          rentalInvoice: null,
          generalInvoice: null,
          ...emptyInvoiceSelection,
        });
        break;

      case "rentalInvoice":
        if (!value) {
          ref.current.setFormState(emptyInvoiceSelection);
          break;
        }
        applyRentalInvoice(value);
        break;

      case "generalInvoice":
        if (!value) {
          ref.current.setFormState(emptyInvoiceSelection);
          break;
        }
        applyGeneralInvoice(value);
        break;

      default:
        break;
    }
  };

  return (
    <BoxedContent>
      <DynamicFormContainer
        ref={ref}
        objectName={dynamicObjectMap.get("CreditNoteObjectName")}
        showHeader
        showLinkedViews
        onStateChange={onStateChange}
      />
    </BoxedContent>
  );
}

export default CreditNoteForm;
